import { type ReactNode } from 'react';
import { useScrolled } from '@/hooks/ui/useStickyPin';
import styles from './page-header.module.less';

export interface PageHeaderProps {
  /** Left action area - typically a back button or settings */
  leftAction?: ReactNode;
  /** Right action area - typically share, edit, delete buttons */
  rightAction?: ReactNode;
  /** Optional centered content (title, etc.) */
  children?: ReactNode;
  /** Additional className */
  className?: string;
  /** Vertical alignment of header items - 'center' (default) or 'end' (bottom) */
  alignItems?: 'center' | 'end';
  /** Top padding in pixels - 0 by default */
  paddingTop?: number;
  /** Render the built-in clearance spacer. On (default) when the host page
   *  contributes the 32px design baseline; off when the host owns its full
   *  header clearance itself (e.g. FormPageLayout). */
  spacer?: boolean;
}

export function PageHeader({
  leftAction,
  rightAction,
  children,
  className,
  alignItems = 'center',
  paddingTop = 0,
  spacer = true,
}: PageHeaderProps) {
  // Glass fades in once the page scrolls (mirrors the discovery sticky bar).
  const scrolled = useScrolled();

  return (
    <>
      <div
        data-scrolled={scrolled || undefined}
        className={`${styles.header} ${className || ''}`}
        style={{
          alignItems: alignItems === 'end' ? 'flex-end' : 'center',
          // Top padding: the prop value (if > 0) overrides the CSS default
          // of `padding-top: 16px`. The CSS handles the safe-area + 8px
          // fallback via `max(0px, env(safe-area-inset-top, 0px), 8px)`.
          paddingTop: paddingTop > 0 ? `${paddingTop}px` : undefined,
        }}
      >
        <div className={styles.left}>{leftAction}</div>
        <div className={styles.center}>{children}</div>
        <div className={styles.right}>{rightAction}</div>
      </div>
      {/* Spacer offsets content below the fixed header */}
      {spacer && <div className={styles.headerSpacer} aria-hidden="true" />}
    </>
  );
}
